import { AddCircleOutline } from "@mui/icons-material";
import React, { memo, useEffect, useMemo, useState } from "react";
import { FormControl, FormHelperText, InputLabel, Select as MUISelect, MenuItem, SelectProps } from "@mui/material";

import Box from "../Box";
import TitleValue from "../TitleValue";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";

import { Mode } from "../../constants/enums";
import { ICodename } from "../../commons/interfaces";

interface BaseProps extends SelectProps {
    mode?: Mode;
    data: ICodename[];
    initialValue?: any;
    isFullRow?: boolean;
    errorMessage?: string;
    isAllowCreateNew?: boolean;
    onChangeValue: (value: any, inputKey?: string) => void;
    className?: string;
}

interface IAllowCreateNewProps extends BaseProps {
    isAllowCreateNew: true;
    createNewCallback: VoidFunction;
}

interface IDefaultProps extends BaseProps {
    isAllowCreateNew?: false;
}

type IProps = IDefaultProps | IAllowCreateNewProps;

const ADD_NEW_CODE = "add-new";

/**
 * Function to help typescript to know does the input is or has same fields like IAllowCreateNewProps
 * @param {IProps} input
 * @returns {boolean} input is IAllowCreateNewProps
 */
function isAllowCreateNewProps(input: IProps): input is IAllowCreateNewProps {
    return input.isAllowCreateNew;
}

const SelectBox: React.FC<IProps> = memo((props: IProps) => {
    const strings = useStrings();
    let localProps = { ...props };

    let createNewCallback: VoidFunction | undefined = undefined;

    if (isAllowCreateNewProps(localProps)) {
        createNewCallback = localProps.createNewCallback;
        delete localProps.createNewCallback;
    }

    const { errorMessage, data, onChangeValue, className, initialValue, isAllowCreateNew, ...rest } =
        localProps;

    const [value, setValue] = useState("");

    useEffect(() => {
        if (initialValue !== undefined) {
            setValue(initialValue);
        }
    }, [initialValue]);

    const onChange = (event: any) => {
        const value = event.target.value;
        if (value === ADD_NEW_CODE) {
            createNewCallback?.();
        } else {
            onChangeValue(value);
            setValue(value);
        }
    };

    const selectItems = useMemo(() => {
        const renderItems: React.ReactNode[] = [];
        if (isAllowCreateNew) {
            renderItems.push(
                <MenuItem key={ADD_NEW_CODE} value={ADD_NEW_CODE}>
                    <Box display="flex" alignItems="center" lineHeight={1}>
                        <Typography variant="body1" color="secondary" lineHeight={0.75}>
                            <AddCircleOutline />
                        </Typography>
                        &ensp;
                        <Typography variant="button" fontWeight="regular"
                            sx={{
                                maxWidth: "140px",
                                marginRight: "8px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                            }}>
                            {strings.Common.ADD_NEW}
                        </Typography>
                    </Box>
                </MenuItem>
            );
        }

        data.forEach((item, index) =>
            renderItems.push(
                <MenuItem key={item.code || index.toString()} value={item.code}>
                    <Box display="flex" alignItems="center" lineHeight={1}>
                        <Typography variant="button" fontWeight="regular">
                            {item.name}
                        </Typography>
                    </Box>
                </MenuItem>
            ),
        );

        return renderItems;
    }, [data, isAllowCreateNew, strings]);

    if (props.mode === Mode.View) {
        let val: string[] = [];
        if (props.multiple) {
            initialValue.map((item: any) => val.push(data?.find(el => el.code === item)?.name));
        } else {
            val.push(data.find(el => el.code === initialValue)?.name || "");
        }

        return (
            <TitleValue
                title={`${props.label}`}
                isFullRow={props.isFullRow}
                value={val.filter(item => !Helpers.isNullOrEmpty(item)).join(",")}
            />
        );
    }

    return (
        <FormControl fullWidth variant="standard" className={className} error={!Helpers.isNullOrEmpty(errorMessage)}>
            {
                props.label &&
                <InputLabel id="select-standard-label">
                    {props.required ? `${props.label} *` : props.label}
                </InputLabel>
            }
            <MUISelect
                {...rest}
                id="select-standard"
                sx={{ height: "1.4375em" }}
                labelId="select-standard-labell"
                placeholder={props.placeholder}
                onChange={onChange}
                value={value}
                key={props.initialValue as string || props.label as string}
            >
                {selectItems}
            </MUISelect>
            <FormHelperText>{errorMessage}</FormHelperText>
        </FormControl>
    );
});

SelectBox.displayName = "SelectBox";
SelectBox.defaultProps = {
    isFullRow: false,
    isAllowCreateNew: false,
    className: "w-100",
}

export default SelectBox;
